import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { DollarSign, TrendingUp, PieChart as PieIcon, BarChart3 } from "lucide-react";

const COLORS = ["#065f46", "#047857", "#10b981", "#34d399", "#6ee7b7", "#a7f3d0", "#d1fae5"];

import { fmtUSD } from "../../utils/currency";

export function PortfolioCharts({ commitments = [], distributions = [] }) {
  const stats = useMemo(() => {
    const totalCommitted = commitments.reduce((s, c) => s + (c.amount || 0), 0);
    const totalDistributed = distributions.reduce((s, d) => s + (d.amount || 0), 0);
    const activeCommits = commitments.filter(c => c.status !== "withdrawn");
    const byStatus = {};
    commitments.forEach(c => { byStatus[c.status || "pending"] = (byStatus[c.status || "pending"] || 0) + (c.amount || 0); });
    const byLoanType = {};
    activeCommits.forEach(c => {
      const lt = c.loan_type || c.deal_snapshot?.loan_type || "Other";
      byLoanType[lt] = (byLoanType[lt] || 0) + (c.amount || 0);
    });

    // Distribution timeline (monthly)
    const monthlyDist = {};
    distributions.forEach(d => {
      if (d.created_at || d.distribution_date) {
        const date = new Date(d.created_at || d.distribution_date);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
        monthlyDist[key] = (monthlyDist[key] || 0) + (d.amount || 0);
      }
    });

    return {
      totalCommitted,
      totalDistributed,
      roi: totalCommitted > 0 ? ((totalDistributed / totalCommitted) * 100).toFixed(1) : 0,
      activeCount: activeCommits.length,
      statusData: Object.entries(byStatus).map(([name, value]) => ({ name: name.replace(/_/g, " "), value })),
      loanTypeData: Object.entries(byLoanType).map(([name, value]) => ({ name: name.replace(/_/g, " "), value })),
      distTimeline: Object.entries(monthlyDist).sort().map(([month, amount]) => ({ month: month.slice(5), amount })),
    };
  }, [commitments, distributions]);

  return (
    <div className="space-y-4" data-testid="portfolio-charts">
      {/* Summary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Total Committed", value: fmtUSD(stats.totalCommitted), icon: DollarSign, accent: true },
          { label: "Total Distributions", value: fmtUSD(stats.totalDistributed), icon: TrendingUp },
          { label: "ROI", value: `${stats.roi}%`, icon: TrendingUp, accent: stats.roi > 0 },
          { label: "Active Positions", value: stats.activeCount, icon: PieIcon },
        ].map((kpi, i) => (
          <motion.div key={kpi.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="rounded-xl border border-[#e8e0d0] bg-white p-3">
            <div className="flex items-center gap-2">
              <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${kpi.accent ? "bg-emerald-100" : "bg-gray-100"}`}>
                <kpi.icon className={`h-4 w-4 ${kpi.accent ? "text-emerald-600" : "text-[#888]"}`} />
              </div>
              <div>
                <div className="text-lg font-bold text-[#0A0A0A]">{kpi.value}</div>
                <div className="text-[10px] text-[#888]">{kpi.label}</div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Allocation by Loan Type */}
        {stats.loanTypeData.length > 0 && (
          <div className="rounded-xl border border-[#e8e0d0] bg-white p-4" data-testid="loan-type-chart">
            <h4 className="text-xs font-bold text-[#222] mb-3 flex items-center gap-1.5">
              <PieIcon className="h-3.5 w-3.5 text-emerald-600" /> Allocation by Loan Type
            </h4>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={stats.loanTypeData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} dataKey="value" paddingAngle={2}>
                  {stats.loanTypeData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtUSD(v)} contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e8e0d0" }} />
                <Legend formatter={(v) => <span className="text-[10px] capitalize">{v}</span>} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Commitment Status */}
        {stats.statusData.length > 0 && (
          <div className="rounded-xl border border-[#e8e0d0] bg-white p-4" data-testid="status-chart">
            <h4 className="text-xs font-bold text-[#222] mb-3 flex items-center gap-1.5">
              <BarChart3 className="h-3.5 w-3.5 text-blue-600" /> Commitment Status
            </h4>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={stats.statusData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e8e0d0" />
                <XAxis type="number" tickFormatter={v => fmtUSD(v)} tick={{ fontSize: 10 }} />
                <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => fmtUSD(v)} contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e8e0d0" }} />
                <Bar dataKey="value" fill="#065f46" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Distribution Timeline */}
      {stats.distTimeline.length > 0 && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4" data-testid="distribution-timeline-chart">
          <h4 className="text-xs font-bold text-[#222] mb-3 flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5 text-amber-600" /> Distribution Timeline
          </h4>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={stats.distTimeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e8e0d0" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tickFormatter={v => fmtUSD(v)} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => fmtUSD(v)} contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e8e0d0" }} />
              <Bar dataKey="amount" fill="#d97706" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
